/**
 * The fourth live route (#30 / R7.2) — the parent's approval queue and payday panel, on real data.
 *
 * **What this makes true, and what it deliberately still cannot.**
 *
 * Until now `/approvals` rendered `approvalsFixture`: three invented children, a 50/10/40 split
 * nobody chose, and a named child "held back". Meanwhile F-24 shipped
 * `GET /api/payday/preview` and `POST /api/payday/settle` and **no screen called either**. This
 * route closes that: the queue, the per-child eligible money, approving, holding and settling all
 * go to the server.
 *
 * **Three things on this screen have no server contract, and they are OMITTED rather than
 * invented** — see the table in `lib/approvalsMapping.js`:
 *
 *   - a per-child quest TARGET. The preview carries `eligibleCount` and no target, and the screen
 *     used to turn that into **"3 of 0"**. `Approvals.jsx` was repaired in the same commit.
 *   - jar SPLIT percentages. The preview carries jar amounts in currency, not percentages.
 *   - a HOLD list — until #580 F-08. The preview now says, per child, whether the week was
 *     `complete`, `incomplete` or `unknown`, and a hold card is shown for `incomplete` only
 *     (`lib/approvalsMapping.js` `holdsFrom`). Pay anyway is real, and so are Wait a week (#580 4b2)
 *     and Talk it over (#580 5b) while the server says holds are on.
 *
 * Those three are `#29` (R7.1) work and are named in the delivery record. This route shows the
 * sections it can fill and leaves the rest empty, which is why the screen looks lighter than the
 * fixture gallery. That is the honest state, not a regression.
 *
 * **The vanishing-warning trap, the same one `OnboardingRoute` documents.** `Surface`'s
 * "Preview. Fixture data. No server." banner comes from fixture mode, and a live container leaves
 * fixture mode — so going live silently REMOVES the warning. This route carries its own note
 * instead, so the screen never quietly stops saying what it cannot do.
 *
 * **Both reads here are parent-only.** `GET /api/approvals` and `GET /api/payday/preview` are
 * `requireAdmin`, so a child who reaches this path gets a 403. That is answered with a sentence
 * that ends, rather than a "try again" that sends them round a loop they cannot leave.
 */
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PAY_UNCONFIRMED } from '../state/receipts.js';
import LanternLoader from '../components/LanternLoader.jsx';
import { ApiError, api } from '../api/client.js';
import { settleableUserIds, toApprovalRows, toPaydayPanel } from '../lib/approvalsMapping.js';
import { freshnessLead, useLiveRefresh } from '../state/HouseholdRealtime.jsx';

const Approvals = lazy(() => import('../pages/Approvals.jsx'));

/**
 * #580 F-08 — Pay anyway's sentences when the server did not confirm. Every one begins with the
 * same words, because in every case the parent did not see the payment confirmed. None says
 * "unchanged": after a lost response it may have happened. The second sentence says what is TRUE
 * and what to do next, and a refusal that can never succeed (fix round 1, review M2) never tells
 * the parent to try again.
 */
const UNCONFIRMED = 'We couldn’t confirm the change.';
export const PAY_ANYWAY_MESSAGES = Object.freeze({
  signedOut: `${UNCONFIRMED} Please sign in again before checking this payment.`,
  rateLimited: `${UNCONFIRMED} Please wait before trying again to check this payment.`,
  reauth: `${UNCONFIRMED} Please confirm it is you, then press Pay anyway again.`,
  forbidden: `${UNCONFIRMED} Only a parent can pay a child.`,
  changed: `${UNCONFIRMED} This week changed since you looked, so the screen has been refreshed — check it and try again.`,
  settling: `${UNCONFIRMED} A payment for this child is being settled right now. Check again in a moment.`,
  notMember: `${UNCONFIRMED} This child is no longer a current member of the family, so Pay anyway cannot pay them.`,
  alreadyPaid: `${UNCONFIRMED} This child has already been paid for this week, so nothing more was paid.`,
  nothingToPay: `${UNCONFIRMED} There is no money waiting for this child this week, so there is nothing to pay.`,
  notIncomplete: `${UNCONFIRMED} This child’s week is no longer incomplete, so Pay anyway is not needed — use Settle.`,
  // 503 PAYDAY_BUSY: the server waited for another payday change and wrote nothing for THIS press.
  // Not "nothing was paid": an earlier press whose answer was lost may still be finishing.
  busy: `${UNCONFIRMED} Another payday change was in progress, so this press paid nothing. Try again in a moment.`,
  // True because the card stays on screen while its key is unspent (see `withPinnedHolds`).
  unknown: PAY_UNCONFIRMED,
});

/**
 * The server's refusal codes, each with its sentence and whether it RELEASES the pinned key.
 *
 * Most codes follow a lookup that found no payment under this key. OPERATION_KEY_REUSED
 * instead means the key belongs to a different decision context; it cannot confirm this request.
 * Those refusals release this context's pin. Authentication failures, rate limits, server errors
 * and malformed responses do not establish the payment outcome and keep the original context.
 */
const REFUSALS = Object.freeze({
  CHILD_NOT_MEMBER: 'notMember',
  ALREADY_SETTLED: 'alreadyPaid',
  NOTHING_ELIGIBLE: 'nothingToPay',
  WEEK_NOT_INCOMPLETE: 'notIncomplete',
  PREVIEW_STALE: 'changed',
  PERIOD_CHANGED: 'changed',
  OPERATION_KEY_REUSED: 'changed',
  DRAFT_EXISTS: 'settling',
});

/**
 * #580 F-08 slice 4b2 — Wait a week's sentences when the server did not confirm, on the same rules
 * as Pay anyway's above. "Nothing is held twice" is true because a repeat sends the same key and
 * the server answers with the original hold.
 */
const WAIT_UNCONFIRMED = 'We couldn’t confirm the Wait.';
export const WAIT_MESSAGES = Object.freeze({
  signedOut: `${WAIT_UNCONFIRMED} Please sign in again before checking it.`,
  rateLimited: `${WAIT_UNCONFIRMED} Please wait a little before checking it again.`,
  reauth: `${WAIT_UNCONFIRMED} Please confirm it is you, then press Wait a week again.`,
  forbidden: `${WAIT_UNCONFIRMED} Only a parent can keep a child’s pay for later.`,
  changed: `${WAIT_UNCONFIRMED} This week changed since you looked, so the screen has been refreshed — check it and try again.`,
  notMember: `${WAIT_UNCONFIRMED} This child is no longer a current member of the family, so their pay cannot wait.`,
  nothingToPay: `${WAIT_UNCONFIRMED} There is no money waiting for this child this week, so there is nothing to keep.`,
  notIncomplete: `${WAIT_UNCONFIRMED} This child’s week is no longer incomplete, so there is nothing to decide — use Settle.`,
  alreadyDue: `${WAIT_UNCONFIRMED} This money is already due, so it cannot wait. Use Pay anyway or Settle instead.`,
  notSwitchedOn: `${WAIT_UNCONFIRMED} Wait a week is not switched on for this family yet, so nothing was kept.`,
  busy: `${WAIT_UNCONFIRMED} Another payday change was in progress, so this press kept nothing. Try again in a moment.`,
  unknown: `${WAIT_UNCONFIRMED} Nothing is held twice — press Wait a week again to check what happened.`,
});

const WAIT_REFUSALS = Object.freeze({
  CHILD_NOT_MEMBER: 'notMember',
  NOTHING_ELIGIBLE: 'nothingToPay',
  WEEK_NOT_INCOMPLETE: 'notIncomplete',
  HOLD_ALREADY_DUE: 'alreadyDue',
  HOLDS_NOT_ENABLED: 'notSwitchedOn',
  PREVIEW_STALE: 'changed',
  PERIOD_CHANGED: 'changed',
  OPERATION_KEY_REUSED: 'changed',
});

/**
 * #580 F-08 slice 5b — Talk it over, and "We talked it over", on the same rules. A talk moves no
 * money, so "nothing is held twice" stays true of a repeat with the same key.
 */
const TALK_UNCONFIRMED = 'We couldn’t confirm the Talk.';
// Written out in full, as RESOLVE_MESSAGES is (review L4): one table serves the card AND the "Kept
// for later" list, so `changed` names neither a week nor a hold, and no sentence speaks of a Wait.
export const TALK_MESSAGES = Object.freeze({
  signedOut: `${TALK_UNCONFIRMED} Please sign in again before checking it.`,
  rateLimited: `${TALK_UNCONFIRMED} Please wait a little before checking it again.`,
  reauth: `${TALK_UNCONFIRMED} Please confirm it is you, then press Talk it over again.`,
  forbidden: `${TALK_UNCONFIRMED} Only a parent can ask to talk it over.`,
  changed: `${TALK_UNCONFIRMED} This changed since you looked, so the screen has been refreshed — check it and try again.`,
  notMember: `${TALK_UNCONFIRMED} This child is no longer a current member of the family, so nothing was kept for a talk.`,
  nothingToPay: `${TALK_UNCONFIRMED} There is no money waiting for this child this week, so there is nothing to keep.`,
  notIncomplete: `${TALK_UNCONFIRMED} This child’s week is no longer incomplete, so there is nothing to decide — use Settle.`,
  alreadyDue: `${TALK_UNCONFIRMED} This money is already due, so it cannot be kept for a talk. Use Pay anyway or Settle instead.`,
  notSwitchedOn: `${TALK_UNCONFIRMED} Talk it over is not switched on for this family yet, so nothing was kept.`,
  busy: `${TALK_UNCONFIRMED} Another payday change was in progress, so this press kept nothing. Try again in a moment.`,
  notFound: `${TALK_UNCONFIRMED} That money is no longer kept for this child, so the screen has been refreshed.`,
  closed: `${TALK_UNCONFIRMED} That money has already been paid, so the talk is no longer needed before payday.`,
  unknown: `${TALK_UNCONFIRMED} Nothing is held twice — press Talk it over again to check what happened.`,
});
export const HELD_TALK_MESSAGES = Object.freeze({
  ...TALK_MESSAGES,
  unknown: `${TALK_UNCONFIRMED} Check the Kept for later list for the current talk and payment status.`,
});
const TALK_REFUSALS = Object.freeze({ ...WAIT_REFUSALS, HOLD_NOT_FOUND: 'notFound', HOLD_CLOSED: 'closed' });

const RESOLVE_UNCONFIRMED = 'We couldn’t confirm that the talk is marked as done.';
export const RESOLVE_MESSAGES = Object.freeze({
  signedOut: `${RESOLVE_UNCONFIRMED} Please sign in again before checking it.`,
  rateLimited: `${RESOLVE_UNCONFIRMED} Please wait a little before checking it again.`,
  reauth: `${RESOLVE_UNCONFIRMED} Please confirm it is you, then press We talked it over again.`,
  forbidden: `${RESOLVE_UNCONFIRMED} Only a parent can mark a talk as done.`,
  changed: `${RESOLVE_UNCONFIRMED} This changed since you looked, so the screen has been refreshed — check it and try again.`,
  notFound: `${RESOLVE_UNCONFIRMED} That money is no longer kept for this child, so the screen has been refreshed.`,
  closed: `${RESOLVE_UNCONFIRMED} That money has already been paid, and paying it ended the talk.`,
  notPending: `${RESOLVE_UNCONFIRMED} There is no talk waiting for this money, so nothing changed.`,
  notMember: `${RESOLVE_UNCONFIRMED} This child is no longer a current member of the family, so nothing changed.`,
  busy: `${RESOLVE_UNCONFIRMED} Another payday change was in progress, so nothing changed. Try again in a moment.`,
  unknown: `${RESOLVE_UNCONFIRMED} Check the Kept for later list for the current talk and payment status.`,
});
const RESOLVE_REFUSALS = Object.freeze({
  PREVIEW_STALE: 'changed', OPERATION_KEY_REUSED: 'changed', HOLD_NOT_FOUND: 'notFound', HOLD_CLOSED: 'closed',
  TALK_NOT_PENDING: 'notPending', CHILD_NOT_MEMBER: 'notMember',
});

/** What a failed request says, from `messages`, and whether its key is released (`refusals`). */
function refusalFor(error, messages, refusals) {
  if (error instanceof ApiError && (error.status === 401 || error.status === 429)) {
    return { message: error.status === 401 ? messages.signedOut : messages.rateLimited, release: false };
  }
  if (error instanceof ApiError && error.status === 403) {
    return {
      message: error.body && error.body.code === 'PARENT_REAUTH_REQUIRED' ? messages.reauth : messages.forbidden,
      release: false,
    };
  }
  const code = error instanceof ApiError && error.body ? error.body.code : undefined;
  // A prior press may still be in flight. Keep its exact key and pinned recovery context.
  if (code === 'PAYDAY_BUSY') return { message: messages.busy, release: false };
  if (typeof code === 'string' && Object.prototype.hasOwnProperty.call(refusals, code)) {
    return { message: messages[refusals[code]], release: true };
  }
  return { message: messages.unknown, release: false };
}

/** What a failed Pay anyway request says, and whether its key is released. */
const payAnywayRefusal = (error) => refusalFor(error, PAY_ANYWAY_MESSAGES, REFUSALS);

/**
 * An unresolved payment keeps its original context visible until reconciliation, even across
 * a new payday window. Offer that recovery before another payment for the same child. A refreshed
 * amount or revision must never silently replace what the parent already sent under this key.
 */
function withPinnedHolds(panel, pins) {
  if (!pins.size) return panel;
  const kept = [...pins.values()].map((p) => ({ ...p.hold, recovering: true, recoveringKind: p.kind || 'pay' }));
  const recoveringChildren = new Set(kept.map((h) => h.id));
  return { ...panel, holds: [...panel.holds.filter((h) => !recoveringChildren.has(h.id)), ...kept] };
}

/**
 * #580 5b — the request a "Kept for later" action sends, pinned per hold in `keys` until confirmed.
 * A repeat after a lost response sends the SAME key and the SAME revision: the request first made.
 */
function heldRequestFor(keys, slot, h, prefix) {
  if (!keys.has(slot)) {
    keys.set(slot, { holdId: h.holdId, userId: h.id, holdRevision: h.holdRevision, operationKey: newOperationKey(h.id, prefix) });
  }
  return keys.get(slot);
}

/** Send it; a refusal that settles the question releases the key. The screen re-reads either way. */
async function postHeld(keys, slot, path, body, { messages, refusals, reload }) {
  try {
    return await api.post(path, body);
  } catch (error) {
    const refusal = refusalFor(error, messages, refusals);
    if (refusal.release) keys.delete(slot);
    throw reportable(refusal.message);
  } finally {
    reload();
  }
}

/** A new caller-held operation id. `crypto.randomUUID` where the browser has it. */
function newOperationKey(userId, prefix = 'pay-anyway') {
  const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${userId}-${random}`;
}

const cents = (value) => Math.round(amountOf(value) * 100);
const HOLD_STATUSES = new Set(['open', 'released', 'payday_changed']);
const TALK_STATES = new Set(['pending', 'resolved', 'released', 'payday_changed']);
/** The hold's talk, only in a state the database allows. */
const talkOf = (kept) => (kept && kept.conversation && typeof kept.conversation === 'object'
  && TALK_STATES.has(kept.conversation.state) && typeof kept.conversation.operationKey === 'string'
  ? kept.conversation : null);
/** Bind a receipt, including a closed replay, to the listed hold and its immutable basis. */
const isHeldReceipt = (kept, h) => !!kept && kept.holdId === h.holdId && kept.userId === h.id
  && HOLD_STATUSES.has(kept.status) && cents(kept.total) === cents(h.total)
  && kept.currency === h.currency && kept.periodStart === h.periodStart && kept.periodEnd === h.periodEnd
  && kept.releaseAt === h.releaseAt && kept.timezone === h.timezone;

/** Compare the receipt's exact approval set, accepting order changes but never duplicates. */
function sameApprovals(actual, expected) {
  if (!Array.isArray(actual) || !Array.isArray(expected) || !expected.length) return false;
  const valid = (ids) => ids.every((id) => Number.isSafeInteger(id) && id > 0) && new Set(ids).size === ids.length;
  return valid(actual) && valid(expected) && actual.length === expected.length
    && expected.every((id) => actual.includes(id));
}

/** The three per-jar amounts must match the preview, not merely add to the same total. */
function sameJars(actual, expected) {
  return !!actual && !!expected && ['save', 'give', 'spend'].every((key) => {
    const value = actual[key];
    return (typeof value === 'number' || (typeof value === 'string' && /^\d+\.\d{2}$/.test(value)))
      && Number.isFinite(Number(value)) && Number(value) >= 0
      && cents(value) / 100 === Number(value)
      && cents(value) === cents(expected[key]);
  });
}


/**
 * Carried in the payday caption, because leaving fixture mode removes the fixture banner. It opens
 * with `freshnessLead(status)`: "Live family data." only while the screen is live (#580).
 */
export const PARTIAL_NOTICE =
  'Per-child quest targets and changing a reward amount are not built yet, so those sections stay empty.';

/** Said when a background read kept the last good preview or window because the new one failed. */
export const PAYDAY_STALE =
  'The payday panel shows the last payday read, because the newest one could not be loaded, so it may be out of date.';

export const LOAD_FAILED =
  "We couldn't load the approval queue just now — give it another moment.";

/** Said when the preview read failed on its own. The queue is still on screen and still true. */
export const PAYDAY_UNREAD =
  "We couldn't read this week's payday just now, so the earnings panel is empty. The approval queue below is up to date.";

/** Said when Settle is pressed with nobody left to pay. Silence would look like a dead button. */
export const NOBODY_TO_SETTLE =
  'There is nobody to pay this week — every child with money waiting has already been settled.';

/**
 * An outcome the parent must be told in THESE words, not in the screen's generic refusal.
 *
 * "We couldn't confirm that change. The previous state is unchanged." is the right sentence when
 * nothing happened. It is a false sentence when three children were paid and one was blocked, and
 * that is the case this exists for: `Approvals.jsx`'s `confirmWrite` prefers `userMessage` when
 * the host supplies one.
 */
function reportable(message) {
  const error = new Error(message);
  error.userMessage = message;
  return error;
}

/** `'7.25'` and `7.25` both have to become a number the receipt matcher can compare. */
function amountOf(value) {
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * A receipt id derived from what the SERVER said, never from what we asked for.
 *
 * `POST /api/approvals/:id/approve` and `POST /api/payday/settle` return no `receiptId` field, and
 * `state/receipts.js` requires one before the screen will believe a write. Deriving it from the
 * server's own decided-at / settled-at stamp keeps the guarantee that matters: the id cannot be
 * built unless the server answered with a persisted row.
 */
function receiptIdFrom(kind, id, at) {
  return `${kind}-${id}-${at}`;
}

/**
 * One sentence for a failed LOAD. A 403 is its own answer and must not offer a retry: both reads
 * are parent-only, so a child retrying can only fail again.
 */
export function loadMessage(error) {
  if (!(error instanceof ApiError)) return LOAD_FAILED;
  if (error.status === 403) return 'Only a parent can review the approval queue.';
  if (error.status === 401) return 'Your sign-in has expired — please sign in again.';
  return LOAD_FAILED;
}

/** One sentence for a failed ACTION, kept apart from a failed load. */
export function actionMessage(error, what) {
  if (error instanceof ApiError && error.status === 403) {
    return `Only a parent can ${what}.`;
  }
  if (error instanceof ApiError && error.status === 401) {
    return 'Your sign-in has expired — please sign in again.';
  }
  // The server waited for another payday change, gave up and wrote nothing (`paydayLock.js`).
  if (error instanceof ApiError && error.body && error.body.code === 'PAYDAY_BUSY') {
    return `We couldn't ${what} — another payday change was in progress, so nothing was changed. Try again in a moment.`;
  }
  return `We couldn't ${what} just now — please try again in a moment.`;
}

/**
 * Read the three payloads together.
 *
 * Only the QUEUE is allowed to fail the screen. The parent came here to review approvals, and
 * losing the queue that loaded because the payday preview 5xx'd would mean answering the one read
 * that worked with a sentence about the one that did not. The preview and the window each degrade
 * on their own instead, and the panel says which part is missing.
 */
async function loadAll(signal, refused = new Map()) {
  // Still one round trip's worth of wall clock — the three go out together. What changed is that
  // only the queue's rejection reaches the caller; the other two are folded into a settled result
  // so a 5xx on either cannot reject this `Promise.all`.
  const settled = (path) => {
    if (refused.has(path)) return Promise.resolve({ ok: false, error: refused.get(path) });
    return api.get(path, { signal }).then((value) => ({ ok: true, value }), (error) => {
      if (!signal?.aborted && error instanceof ApiError && error.status === 403) refused.set(path, error);
      return { ok: false, error };
    });
  };
  const [queue, preview, window] = await Promise.all([
    api.get('/api/approvals', { signal }),
    settled('/api/payday/preview'),
    settled('/api/payday/window'),
  ]);
  return {
    queue,
    preview: preview.ok ? preview.value : null,
    previewFailed: !preview.ok,
    previewError: preview.error,
    windowFailed: !window.ok,
    windowError: window.error,
    window: window.ok && window.value && window.value.window ? window.value.window : null,
  };
}

export default function ApprovalsRoute() {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [reloadKey, setReloadKey] = useState(0);
  const refusedPayday = useRef(new Map());

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    loadAll(controller.signal, refusedPayday.current)
      .then((data) => { if (live) setState({ status: 'ready', data, error: null }); })
      .catch((error) => {
        if (!live || controller.signal.aborted) return;
        setState({ status: 'error', data: null, error });
      });
    return () => { live = false; controller.abort(); };
  }, [reloadKey]);

  const reload = useCallback(() => {
    setReloadKey((n) => n + 1);
  }, []);

  /**
   * #580: the same three reads again when the household changes, in the BACKGROUND. `reload()`
   * would put the page back on its loader and throw away a reason the parent is typing, so an
   * event never uses it. Pinned operation keys live outside this data and survive it, and an event
   * never confirms a write — only that write's own receipt does.
   *
   * Payday's Wait, Talk and "We talked it over" send no event (F-08, parent-only on purpose), so
   * another parent's screen learns of them from the 30-second re-read. That is not instant.
   *
   * Design §3: queue, preview and window keep their OWN error states, and a transient failure keeps
   * the last good snapshot (review q1 H3). So a failed preview or window keeps the one on screen,
   * marked `paydayStale`, and `previewFailed` stays false — the panel is not empty. A queue that
   * comes back unreadable changes nothing at all.
   */
  const live = useLiveRefresh('approvals', {
    read: (signal) => loadAll(signal, refusedPayday.current),
    apply: (data) => {
      if (!data.queue || !Array.isArray(data.queue.approvals)) return false;
      setState((prev) => {
        const old = prev.data || {};
        const keepPreview = data.previewFailed && ![401, 403].includes(data.previewError?.status) && !!old.preview;
        const keepWindow = data.windowFailed && ![401, 403].includes(data.windowError?.status) && !!old.window;
        return { status: 'ready', error: null, data: {
          ...data,
          preview: keepPreview ? old.preview : data.preview,
          previewFailed: keepPreview ? false : data.previewFailed,
          window: keepWindow ? old.window : data.window,
          paydayStale: keepPreview || keepWindow,
        } };
      });
      return true;
    },
    enabled: state.status === 'ready',
  });

  const { queue, preview, window, previewFailed, paydayStale } = state.data || {};
  const approvals = useMemo(() => toApprovalRows(queue), [queue]);
  // Pay anyway's unspent operation keys, by child and window: `{ key, hold }`. The ref is read
  // synchronously by the request; the state copy re-renders the screen (see `withPinnedHolds`).
  const payKeys = useRef(new Map());
  const [pins, setPins] = useState(() => new Map());
  const pin = useCallback((slot, entry) => {
    payKeys.current.set(slot, entry);
    setPins(new Map(payKeys.current));
  }, []);
  const unpin = useCallback((slot) => {
    payKeys.current.delete(slot);
    setPins(new Map(payKeys.current));
  }, []);
  const payday = useMemo(
    () => withPinnedHolds(toPaydayPanel(preview, window), pins),
    [preview, window, pins],
  );

  /**
   * Approve one quest, then reload, then hand back the server's own receipt.
   *
   * **Three things here were each a defect.** The parameter is the row `id`, not the row: the
   * screen calls `onApprove(id)`, and a guard on `item.id` against a primitive returned before any
   * request went out, so the button was inert. The receipt is read from the server's answer rather
   * than assumed: `state/receipts.js` refuses a write the host cannot evidence, and a callback that
   * resolved to `undefined` told the parent nothing changed while the row was already approved.
   * And the reload is not laziness — approving moves money into the payday preview, so the panel
   * below the queue is stale the moment the row is gone.
   */
  const onApprove = useCallback(async (id) => {
    if (id == null) return undefined;
    let answer;
    try {
      answer = await api.post(`/api/approvals/${encodeURIComponent(id)}/approve`, {});
    } catch (error) {
      throw reportable(actionMessage(error, 'approve that quest'));
    }
    reload();
    const approval = answer && answer.approval;
    // A 200 is not a decision. Only an approval the server says is `approved` may be reported as
    // approved, and `decidedAt` is the stamp that proves the row was written.
    if (!approval || approval.status !== 'approved' || !approval.decidedAt) return undefined;
    return {
      receiptId: receiptIdFrom('approval', approval.id, approval.decidedAt),
      status: 'approved',
      id: Number(approval.id),
      amount: amountOf(approval.rewardAmount),
    };
  }, [reload]);

  /**
   * Hold one quest, with the reason the server requires.
   *
   * `approval.service.js` refuses a rejection with no reason (400, "A hold needs a reason so the
   * child is told why"), so the screen collects one and sends it. The server stores the status as
   * `rejected`; this screen calls it `held`, so the receipt translates rather than echoes.
   */
  const onHold = useCallback(async (id, reason) => {
    if (id == null) return undefined;
    const trimmed = typeof reason === 'string' ? reason.trim() : '';
    if (!trimmed) throw reportable('A hold needs a reason, so the child is told why.');
    let answer;
    try {
      answer = await api.post(`/api/approvals/${encodeURIComponent(id)}/reject`, { reason: trimmed });
    } catch (error) {
      throw reportable(actionMessage(error, 'hold that quest'));
    }
    reload();
    const approval = answer && answer.approval;
    if (!approval || approval.status !== 'rejected' || !approval.decidedAt) return undefined;
    return {
      receiptId: receiptIdFrom('approval', approval.id, approval.decidedAt),
      status: 'held',
      id: Number(approval.id),
    };
  }, [reload]);

  /**
   * Approve every pending row.
   *
   * `decideBulk` is conflict-tolerant on purpose: a row another device decided a second earlier
   * comes back in `skipped` rather than failing the batch. So `skipped` is REPORTED. Folding it
   * into the success count would tell a parent that twenty quests were approved when nineteen
   * were, and the screen's matcher would not catch it either.
   */
  const onApproveAll = useCallback(async (ids) => {
    const approvalIds = (Array.isArray(ids) ? ids : []).filter((id) => Number.isFinite(Number(id)));
    if (approvalIds.length === 0) return undefined;
    let answer;
    try {
      answer = await api.post('/api/approvals/bulk', { approvalIds, decision: 'approve' });
    } catch (error) {
      throw reportable(actionMessage(error, 'approve everything'));
    }
    reload();
    const decided = Array.isArray(answer && answer.decided) ? answer.decided.map(Number) : [];
    const skipped = Array.isArray(answer && answer.skipped) ? answer.skipped : [];
    if (skipped.length > 0 || decided.length !== approvalIds.length) {
      throw reportable(
        `${decided.length} of ${approvalIds.length} approved — the rest were already decided somewhere else.`
      );
    }
    return {
      receiptId: receiptIdFrom('bulk', decided.join('.'), answer.status || 'approved'),
      status: 'approved',
      ids: decided,
    };
  }, [reload]);

  /**
   * Settle the current window.
   *
   * `userIds` names only the children who have something eligible and are not already settled.
   * Sending everybody would ask the server to settle a child twice, which it correctly reports as
   * a per-child conflict — a confusing answer to a question we did not need to ask.
   *
   * **The per-child `results` are read, not discarded.** `payday.routes.js` returns 200 with one
   * result per child precisely because a batch where one child is blocked still settles the
   * others, "and the caller has to be told which is which". A screen that ignores that opens the
   * payday festival while one child's payout was refused.
   */
  const onSettle = useCallback(async () => {
    const userIds = settleableUserIds(preview);
    // The schema refuses an empty array (`min(1)`), so this never reaches the wire — but a button
    // that silently does nothing reads as broken, so say what is true instead.
    if (userIds.length === 0) throw reportable(NOBODY_TO_SETTLE);
    let answer;
    try {
      answer = await api.post('/api/payday/settle', { userIds });
    } catch (error) {
      throw reportable(actionMessage(error, 'settle payday'));
    }
    reload();
    const results = Array.isArray(answer && answer.results) ? answer.results : [];
    const paid = results.filter((r) => r && (r.status === 'settled' || r.status === 'idempotent'));
    // BOTH conditions, not just the count. A `blocked` result alongside a missing one would leave
    // the two totals equal by coincidence, and the parent would be told payday landed in full.
    if (paid.length !== userIds.length || paid.length !== results.length) {
      throw reportable(
        `${paid.length} of ${userIds.length} children were paid — the rest could not be paid this time.`
      );
    }
    const settledAt = paid.map((r) => r.settledAt).find(Boolean);
    if (!settledAt) return undefined;
    return {
      receiptId: receiptIdFrom('payday', userIds.join('.'), settledAt),
      status: 'settled',
      // `toPaydayPanel` never sets a week, so the screen's `week` is null and the matcher compares
      // against null. Sending a number here would fail the match on a settlement that worked.
      week: payday.week != null ? payday.week : null,
      currency: payday.currency,
      total: paid.reduce((sum, r) => sum + (amountOf(r.total) || 0), 0),
    };
  }, [payday.currency, payday.week, preview, reload]);

  /**
   * #580 F-08 slice 3 — pay ONE child whose week was incomplete, and record the choice.
   *
   * **The operation key is pinned per child and window until a payment is CONFIRMED.** A response
   * that never arrived may still have paid. Pressing again therefore sends the same key, and the
   * server answers with the original decision and receipt instead of paying a second time
   * (design D-2, D-7). A fresh key per press is exactly the "repeat the money operation blindly"
   * this exists to prevent.
   *
   * **A 200 is not a payment.** The receipt must be ONE result, for THIS child, settled (or the
   * replay of that settlement), with a settlement stamp, AND a `pay_anyway` decision for this child
   * carrying the key we sent. Anything else is reported as unconfirmed. The amount is checked by
   * the screen against the figure it showed the parent.
   */
  const onPayAnyway = useCallback(async (hold) => {
    if (!hold || hold.id == null) return undefined;
    const slot = `${hold.id}:${hold.periodStart}`;
    if (!payKeys.current.has(slot)) pin(slot, { key: newOperationKey(hold.id), hold: { ...hold, currency: payday.currency } });
    const { key: operationKey, hold: original } = payKeys.current.get(slot);
    let answer;
    try {
      answer = await api.post('/api/payday/settle', {
        decision: {
          kind: 'pay_anyway',
          userId: original.id,
          periodStart: original.periodStart,
          periodEnd: original.periodEnd,
          previewRevision: original.previewRevision,
          operationKey,
        },
      });
    } catch (error) {
      reload();
      const refusal = payAnywayRefusal(error);
      if (refusal.release) unpin(slot);
      throw reportable(refusal.message);
    }
    reload();
    const results = Array.isArray(answer && answer.results) ? answer.results : [];
    const receipt = results.length === 1 ? results[0] : null;
    const decision = answer && answer.decision;
    const confirmed = !!receipt
      && receipt.userId === original.id
      && Number.isSafeInteger(receipt.payoutId) && receipt.payoutId > 0
      && (receipt.status === 'settled' || receipt.status === 'idempotent')
      && typeof receipt.settledAt === 'string' && receipt.settledAt !== ''
      && !!decision
      && decision.kind === 'pay_anyway'
      && decision.userId === original.id
      && Number.isSafeInteger(decision.id) && decision.id > 0
      && receipt.periodStart === original.periodStart && receipt.periodEnd === original.periodEnd
      && decision.periodStart === original.periodStart && decision.periodEnd === original.periodEnd
      && decision.previewRevision === original.previewRevision
      && receipt.currency === original.currency
      && decision.operationKey === operationKey
      && decision.payoutId === receipt.payoutId
      // The amount the parent was shown, in whole cents. The screen checks it again; it is checked
      // HERE too so the pinned key is only released for a payment that matched in every respect.
      && Math.round(amountOf(receipt.total) * 100) === Math.round(amountOf(original.earned) * 100);
    if (!confirmed) throw reportable(PAY_ANYWAY_MESSAGES.unknown);
    unpin(slot);
    return {
      receiptId: receiptIdFrom('pay-anyway', receipt.payoutId, receipt.settledAt),
      status: 'settled',
      userId: receipt.userId,
      total: amountOf(receipt.total),
    };
  }, [payday.currency, pin, reload, unpin]);

  /**
   * #580 F-08 slice 4b2 — keep ONE child's incomplete-week money for later ("Wait a week").
   *
   * The key is pinned per child and window, exactly as Pay anyway's is, until the server confirms:
   * a repeat after a lost response sends the SAME request and gets the original hold back.
   *
   * **A 200 is not a hold.** It must be a `wait` hold for THIS child, THIS window and currency, with
   * a deadline, in a status the database allows. `created` and `replayed` must carry our key and the
   * amount the parent was shown. `existing` means the money was already kept by another request, so
   * it carries that request's key, and it is reported as already kept, never as a new hold.
   */
  const onWait = useCallback(async (hold) => {
    if (!hold || hold.id == null) return undefined;
    const slot = `wait:${hold.id}:${hold.periodStart}`;
    if (!payKeys.current.has(slot)) {
      pin(slot, { kind: 'wait', key: newOperationKey(hold.id, 'wait'), hold: { ...hold, currency: payday.currency } });
    }
    const { key: operationKey, hold: original } = payKeys.current.get(slot);
    let answer;
    try {
      answer = await api.post('/api/payday/holds', {
        kind: 'wait',
        userId: original.id,
        periodStart: original.periodStart,
        periodEnd: original.periodEnd,
        previewRevision: original.previewRevision,
        operationKey,
      });
    } catch (error) {
      reload();
      const refusal = refusalFor(error, WAIT_MESSAGES, WAIT_REFUSALS);
      if (refusal.release) unpin(slot);
      throw reportable(refusal.message);
    }
    reload();
    const kept = answer && answer.hold;
    const outcome = answer && answer.status;
    const ours = outcome === 'created' || outcome === 'replayed';
    const confirmed = answer?.success === true && !!kept && (ours || outcome === 'existing')
      && kept.kind === 'wait'
      && kept.userId === original.id
      && Number.isSafeInteger(kept.holdId) && kept.holdId > 0
      && HOLD_STATUSES.has(kept.status)
      && kept.periodStart === original.periodStart && kept.periodEnd === original.periodEnd
      && kept.currency === original.currency
      && typeof kept.releaseAt === 'string' && Number.isFinite(Date.parse(kept.releaseAt))
      && (ours
        ? kept.operationKey === operationKey && cents(kept.total) === cents(original.earned)
          && sameApprovals(kept.approvalIds, original.approvalIds)
          && sameJars(kept.jars, original.jars)
        : typeof kept.operationKey === 'string' && kept.operationKey !== operationKey && cents(kept.total) > 0);
    if (!confirmed) throw reportable(WAIT_MESSAGES.unknown);
    unpin(slot);
    return {
      receiptId: receiptIdFrom('wait', kept.holdId, kept.releaseAt),
      status: 'held',
      userId: kept.userId,
      total: amountOf(kept.total),
      releaseAt: kept.releaseAt,
      timezone: typeof kept.timezone === 'string' ? kept.timezone : undefined,
      // `paidSince`: a replay of a Wait whose hold has since been paid (the database closes a hold
      // only with its settled payout). `alreadyKept`: another request's hold, unchanged.
      outcome: !ours ? 'alreadyKept' : kept.status === 'open' ? 'kept' : 'paidSince',
    };
  }, [payday.currency, pin, reload, unpin]);

  /**
   * #580 F-08 slice 5b — Talk it over on the incomplete-week card: the money waits exactly as a Wait's
   * does, and the parents plan a talk. The key is pinned exactly as Wait's is.
   *
   * **A 200 is not a Talk.** The hold must name THIS child, THIS window and currency, with a deadline and
   * a talk carrying our key. `created` is a new Talk hold, at the amount the parent was shown.
   * `converted` put our talk on this window's open Wait (its amount and deadline are the Wait's).
   * `existing` is a talk another request opened, told as already planned.
   */
  const onTalk = useCallback(async (hold) => {
    if (!hold || hold.id == null) return undefined;
    const slot = `talk:${hold.id}:${hold.periodStart}`;
    if (!payKeys.current.has(slot)) {
      pin(slot, { kind: 'talk', key: newOperationKey(hold.id, 'talk'), hold: { ...hold, currency: payday.currency } });
    }
    const { key: operationKey, hold: original } = payKeys.current.get(slot);
    let answer;
    try {
      answer = await api.post('/api/payday/holds', {
        kind: 'talk',
        userId: original.id,
        periodStart: original.periodStart,
        periodEnd: original.periodEnd,
        previewRevision: original.previewRevision,
        operationKey,
      });
    } catch (error) {
      reload();
      const refusal = refusalFor(error, TALK_MESSAGES, TALK_REFUSALS);
      if (refusal.release) unpin(slot);
      throw reportable(refusal.message);
    }
    reload();
    const kept = answer && answer.hold;
    const outcome = answer && answer.status;
    const talk = talkOf(kept);
    const ours = outcome === 'created' || outcome === 'converted' || outcome === 'replayed';
    // Made by THIS request: a new Talk hold under our key. Otherwise our key is only on its talk.
    const madeHere = !!kept && kept.operationKey === operationKey;
    const confirmed = !!kept && !!talk && (ours || outcome === 'existing')
      && kept.userId === original.id
      && Number.isSafeInteger(kept.holdId) && kept.holdId > 0
      && HOLD_STATUSES.has(kept.status)
      && kept.periodStart === original.periodStart && kept.periodEnd === original.periodEnd
      && kept.currency === original.currency
      && typeof kept.releaseAt === 'string' && Number.isFinite(Date.parse(kept.releaseAt))
      && cents(kept.total) > 0
      && (ours
        ? talk.operationKey === operationKey && (madeHere
          ? outcome !== 'converted' && kept.kind === 'talk' && cents(kept.total) === cents(original.earned)
          : outcome !== 'created')
        : talk.operationKey !== operationKey);
    if (!confirmed) throw reportable(TALK_MESSAGES.unknown);
    unpin(slot);
    return {
      receiptId: receiptIdFrom('talk', kept.holdId, kept.releaseAt),
      status: 'held',
      userId: kept.userId,
      total: amountOf(kept.total),
      releaseAt: kept.releaseAt,
      timezone: typeof kept.timezone === 'string' ? kept.timezone : undefined,
      outcome: kept.status !== 'open' ? 'paidSince' : talk.state === 'resolved' ? 'alreadyResolved'
        : !ours ? 'alreadyTalking' : madeHere ? 'kept' : 'nowTalking',
    };
  }, [payday.currency, pin, reload, unpin]);

  // "Kept for later" actions, keyed per hold until the server confirms (see `heldRequestFor`).
  const heldKeys = useRef(new Map());

  /** #580 5b — Wait, then Talk: one open hold on the "Kept for later" list gets a talk. No money moves. */
  const onTalkHeld = useCallback(async (h) => {
    if (!h || !Number.isSafeInteger(h.holdId) || typeof h.holdRevision !== 'string') return undefined;
    const slot = `talk-held:${h.holdId}`;
    const request = heldRequestFor(heldKeys.current, slot, h, 'talk');
    const answer = await postHeld(heldKeys.current, slot, '/api/payday/holds', { kind: 'talk', ...request },
      { messages: HELD_TALK_MESSAGES, refusals: TALK_REFUSALS, reload });
    const kept = answer && answer.hold;
    const talk = talkOf(kept);
    const ours = answer && (answer.status === 'converted' || answer.status === 'replayed');
    const confirmed = isHeldReceipt(kept, h) && !!talk
      && (ours ? talk.operationKey === request.operationKey : answer.status === 'existing' && talk.operationKey !== request.operationKey);
    if (!confirmed) throw reportable(HELD_TALK_MESSAGES.unknown);
    heldKeys.current.delete(slot);
    return {
      receiptId: receiptIdFrom('talk', kept.holdId, talk.openedAt), status: 'talking', userId: kept.userId, holdId: kept.holdId,
      outcome: kept.status !== 'open' ? 'paidSince' : talk.state === 'resolved' ? 'alreadyResolved'
        : !ours ? 'alreadyTalking' : 'nowTalking',
    };
  }, [reload]);

  /** #580 5b — "We talked it over": the talk is done. NOT a payment: the money stays until its date. */
  const onResolveTalk = useCallback(async (h) => {
    if (!h || !Number.isSafeInteger(h.holdId) || typeof h.holdRevision !== 'string') return undefined;
    const slot = `resolve:${h.holdId}`;
    const request = heldRequestFor(heldKeys.current, slot, h, 'talk-done');
    const answer = await postHeld(heldKeys.current, slot, '/api/payday/holds/resolve', request,
      { messages: RESOLVE_MESSAGES, refusals: RESOLVE_REFUSALS, reload });
    const kept = answer && answer.hold;
    const talk = talkOf(kept);
    const confirmed = answer && (answer.status === 'resolved' || answer.status === 'replayed')
      && isHeldReceipt(kept, h) && !!talk && talk.state === 'resolved' && talk.resolutionKey === request.operationKey
      && typeof talk.resolvedAt === 'string' && Number.isFinite(Date.parse(talk.resolvedAt));
    if (!confirmed) throw reportable(RESOLVE_MESSAGES.unknown);
    heldKeys.current.delete(slot);
    return { receiptId: receiptIdFrom('talk-done', kept.holdId, talk.resolvedAt), status: 'resolved',
      userId: kept.userId, holdId: kept.holdId, outcome: kept.status === 'open' ? 'held' : 'paidSince' };
  }, [reload]);

  const loadError = state.status === 'error' ? loadMessage(state.error) : null;
  const retryable = !(state.error instanceof ApiError && state.error.status === 403);
  // An ACTION failure is deliberately NOT passed as `error`. `Approvals.jsx` turns any non-empty
  // `error` into a full-page "Couldn't load approvals", which hides the payday panel and offers a
  // "Try again" — a false sentence about a queue that loaded, and a retry that a 409 can never
  // satisfy. Action outcomes go through `confirmWrite`'s live region instead.
  //
  // A failed PREVIEW is its own third thing, and it is not a note appended to the caveat. The
  // first version of this repair let the panel render from an empty preview, which printed
  // "Week total $0.00" and left the Settle button live — a figure and an offer the screen cannot
  // know. `paydayUnavailable` suppresses the panel and says which read failed instead.

  return (
    <Suspense fallback={<LanternLoader label="Loading the approval queue…" />}>
      <Approvals
        showSnapshotNotice={false}
        approvals={approvals}
        payday={payday}
        paydayNote={[freshnessLead(live.status), PARTIAL_NOTICE, paydayStale ? PAYDAY_STALE : ''].filter(Boolean).join(' ')}
        paydayUnavailable={previewFailed ? PAYDAY_UNREAD : null}
        currency={payday.currency}
        loading={state.status === 'loading'}
        error={loadError}
        onRetry={retryable ? reload : undefined}
        onApprove={onApprove}
        onHold={onHold}
        onApproveAll={onApproveAll}
        onSettle={onSettle}
        onPayAnyway={onPayAnyway}
        onWait={onWait}
        onTalk={onTalk}
        onTalkHeld={onTalkHeld}
        onResolveTalk={onResolveTalk}
      />
    </Suspense>
  );
}
